/**
 * PageWrapper component for SEO meta tags and structured data
 * Note: In Next.js, use the Metadata API instead
 * This component is for non-Next.js React apps
 */
export function PageWrapper({
  children,
  title,
  description,
  keywords,
  ogImage,
  ogType = 'website',
  structuredData,
  canonicalUrl,
  noIndex = false,
  className,
}) {
  return (
    <div className={className}>
      {children}
    </div>
  );
}

/**
 * Generate JSON-LD structured data script
 * @param {Object} data - Structured data object
 * @returns {string} JSON-LD script
 */
export function generateStructuredData(data) {
  return JSON.stringify({
    '@context': 'https://schema.org',
    ...data,
  });
}

/**
 * Common structured data templates
 */
export const structuredDataTemplates = {
  organization: (options) => ({
    '@type': 'Organization',
    name: options.name,
    url: options.url,
    logo: options.logo,
    sameAs: options.sameAs || [],
    contactPoint: options.contactPoint,
  }),

  localBusiness: (options) => ({
    '@type': 'LocalBusiness',
    name: options.name,
    image: options.image,
    '@id': options.url,
    url: options.url,
    telephone: options.telephone,
    address: {
      '@type': 'PostalAddress',
      streetAddress: options.streetAddress,
      addressLocality: options.city,
      postalCode: options.postalCode,
      addressCountry: options.country || 'DE',
    },
    openingHoursSpecification: options.openingHours,
  }),

  service: (options) => ({
    '@type': 'Service',
    name: options.name,
    description: options.description,
    provider: {
      '@type': 'Organization',
      name: options.providerName,
    },
    serviceType: options.serviceType,
    areaServed: options.areaServed,
  }),

  article: (options) => ({
    '@type': 'Article',
    headline: options.title,
    description: options.description,
    image: options.image,
    datePublished: options.datePublished,
    dateModified: options.dateModified || options.datePublished,
    author: {
      '@type': 'Person',
      name: options.authorName,
    },
    publisher: {
      '@type': 'Organization',
      name: options.publisherName,
      logo: {
        '@type': 'ImageObject',
        url: options.publisherLogo,
      },
    },
  }),

  breadcrumb: (items) => ({
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  }),

  faqPage: (questions) => ({
    '@type': 'FAQPage',
    mainEntity: questions.map((q) => ({
      '@type': 'Question',
      name: q.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: q.answer,
      },
    })),
  }),
};

export default PageWrapper;
